import React from 'react';
import CompressionProgressBar from './CompressionProgressBar';
import { AnimatedToggle } from './SideMenu';
import './DownloadBar.css';

interface DownloadSectionProps {
  canDownload: boolean;
  onDownloadClick: () => void;
  compressionProgress: number;
  isDownloading: boolean;
  downloadAllIncludingHidden: boolean;
  setDownloadAllIncludingHidden: (value: boolean) => void;
  shouldShowDownloadAllToggle: boolean;
  directWriteEnabled: boolean;
  directWriteReady: boolean;
  directWriteWriting: boolean;
  directWriteProgress: number;
  directWriteFailures: number;
  directWriteHasFailure: boolean;
  directWriteFallback: boolean;
  directWriteHighlight: boolean;
  hasActiveSubscription: boolean;
  hasActiveBatch: boolean;
  onDirectWriteClick: () => void;
  onRetryDirectWrite: () => void;
}

const DownloadSection: React.FC<DownloadSectionProps> = ({
  canDownload,
  onDownloadClick,
  compressionProgress,
  isDownloading,
  downloadAllIncludingHidden,
  setDownloadAllIncludingHidden,
  shouldShowDownloadAllToggle,
  directWriteEnabled,
  directWriteReady,
  directWriteWriting,
  directWriteProgress,
  directWriteFailures,
  directWriteHasFailure,
  directWriteFallback,
  directWriteHighlight,
  hasActiveSubscription,
  hasActiveBatch,
  onDirectWriteClick,
  onRetryDirectWrite,
}) => {
  const directWriteActive = directWriteEnabled;
  const isCreditsOnly = !hasActiveSubscription;
  const requiresAnalysisComplete = directWriteActive && hasActiveBatch;
  const showDirectWriteButton = directWriteActive && (hasActiveSubscription || !directWriteHasFailure);
  const showDownloadButton =
    !directWriteActive || hasActiveSubscription || (isCreditsOnly && directWriteHasFailure);
  const directWriteDisabled = !directWriteReady || directWriteWriting || directWriteFallback;
  const downloadDisabled =
    isDownloading || !canDownload || (requiresAnalysisComplete && !directWriteReady);
  const downloadDescription =
    'Download all images currently shown on Map as a secure ZIP archive. If a filter is active, only filtered images will be downloaded.';
  const directWriteDescription =
    'Write geotagged images directly into the selected folder. If a filter is active, only filtered images will be written.';

  return (
    <section className="download-card download-card--inline">
      <header className="download-card__header">
        <div>
          <p className="download-card__eyebrow">Downloads</p>
          <h3 className="download-card__title">Processed Files</h3>
        </div>
        <span className={`download-chip ${canDownload ? 'download-chip--ready' : ''}`}>
          {canDownload ? 'Ready' : 'Select files'}
        </span>
      </header>
      <div className="download-card__actions download-card__actions--stacked">
        {showDirectWriteButton && (
          <>
            <p className="download-card__description download-card__description--tight">
              {directWriteDescription}
            </p>
            <button
              className={`download-btn download-btn--primary download-btn--full ${directWriteHighlight ? 'is-highlighted' : ''}`}
              onClick={onDirectWriteClick}
              disabled={directWriteDisabled}
            >
              {directWriteWriting ? 'Writing...' : 'Write processed files in-place'}
            </button>
          </>
        )}
        {showDirectWriteButton && directWriteFailures > 0 && hasActiveSubscription && (
          <button
            className="download-btn download-btn--ghost download-btn--full"
            onClick={onRetryDirectWrite}
            disabled={directWriteWriting}
          >
            Retry failed items
          </button>
        )}
        {showDownloadButton && (
          <>
            <p className="download-card__description download-card__description--tight">
              {downloadDescription}
            </p>
            <button
              className="download-btn download-btn--primary download-btn--full"
              onClick={onDownloadClick}
              disabled={downloadDisabled}
            >
              {isDownloading ? 'Preparing download...' : 'Download files'}
            </button>
          </>
        )}
      </div>

      {directWriteWriting && (
        <div className="upload-progress" style={{ marginTop: '0.75rem' }}>
          <div className="upload-progress__label">
            <span>Writing...</span>
            <span className="upload-progress__percent">
              <span className="notranslate">{directWriteProgress}</span>%
            </span>
          </div>
          <div className="upload-progress__bar">
            <div
              className="upload-progress__fill"
              style={{ width: `${directWriteProgress}%` }}
            />
          </div>
        </div>
      )}

      {directWriteActive && directWriteHasFailure && (
        <p className="download-card__status download-card__status--warning">
          Direct Write failed, use "Download files" to download geotagged images.
        </p>
      )}

      {shouldShowDownloadAllToggle && (
        <div className="map-download-toggle map-download-toggle--sidebar">
          <AnimatedToggle
            checked={downloadAllIncludingHidden}
            onChange={setDownloadAllIncludingHidden}
          />
          <div className="map-download-toggle__text">
            <p>Download all images</p>
            <small>Include hidden/unmarked files.</small>
          </div>
        </div>
      )}
      {compressionProgress > 0 && (
        <div className="download-progress">
          <p>Compression progress</p>
          <CompressionProgressBar progress={compressionProgress} />
        </div>
      )}
    </section>
  );
};

export default DownloadSection;
